import React, { useState, useImperativeHandle } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import StoryFileUpload, { StoryFile } from './StoryFileUpload';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import {
  NARRATIVE_STORY_QUESTION_ID,
  NarrativeStoryProps,
  NarrativeSubmitReference,
  StoryValue,
} from './utils';
import { BottomBar } from '../BottomBar';
import IndicatorTextareaRender from 'npo/pages/ReportBuilder/Components/ImpactIndicator/IndicatorTextareaRender';
import { StyledTrueImpactText } from '../ImpactIndicator/IndicatorTextarea';

const isValid = (title: string, description: string): boolean =>
  Boolean(title && description);

const StyledDiv = styled.div`
  padding-bottom: 150px;
  text-align: center;
  .question-text {
    max-width: 730px;
    margin: ${SPACERS.large} auto;
  }
  .sub-heading {
    max-width: 502px;
    margin-left: auto;
    margin-right: auto;
    margin-top: ${SPACERS.medium};
    margin-bottom: ${SPACERS.large};
  }
  .content-wrapper {
    max-width: 550px;
    margin-left: auto;
    margin-right: auto;
    .story-title {
      margin-top: ${SPACERS.small};
      margin-bottom: ${SPACERS.small};
    }
    .story-description {
      height: 150px;
      resize: vertical;
    }
  }
`;

const NarrativeStory = React.forwardRef<
  NarrativeSubmitReference,
  NarrativeStoryProps & {
    isThirdParty: boolean;
    reportId: string | number;
    handleStoryChange: (story: StoryValue) => void;
    handleNext: () => unknown;
    handlePrevious: () => unknown;
  }
>(
  (
    {
      reportId,
      isThirdParty,
      storyFileName,
      storyFileUrl,
      storyFileId,
      storyTitle,
      hideBottomBar,
      storyDescription,
      handlePrevious,
      handleStoryChange,
    },
    forwardRef,
  ) => {
    const [error, setError] = useState<string | undefined>(undefined);
    const [file, setFile] = useState<StoryFile | undefined>({
      file: undefined,
      storyFileId: storyFileId,
      storyFileName: storyFileName,
      storyFileUrl: storyFileUrl,
    });
    const [title, setTitle] = useState<string>(storyTitle || '');
    const [description, setDescription] = useState<string>(
      storyDescription || '',
    );

    const handleSubmit = () => {
      handleStoryChange({
        title: title,
        text: description,
        fileId: file?.storyFileId?.toString(),
        fileName: file?.storyFileName,
        url: file?.storyFileUrl,
      });
    };

    useImperativeHandle(forwardRef, () => ({
      isValid: () => isValid(title, description),
      handleSubmit: () => ({
        title: title,
        text: description,
        fileId: file?.storyFileId?.toString(),
        fileName: file?.storyFileName,
        url: file?.storyFileUrl,
      }),
    }));

    return (
      <>
        <StyledDiv>
          <TrueImpactText
            variant={hideBottomBar ? 'heading-subheading' : 'heading-page'}
            className="question-text"
          >
            Numbers are great but they are most powerful when paired with
            stories. Please share with us a highlight from your work that helps
            to articulate your impact.
          </TrueImpactText>
          <div className="content-wrapper">
            <StoryFileUpload
              reportId={reportId}
              storyFileId={file?.storyFileId?.toString()}
              handleFileUpload={setFile}
              storyFileUrl={file?.storyFileUrl}
              storyFileName={file?.storyFileName}
            />
            <TrueImpactTextfield
              value={title}
              autoFocus={true}
              onChange={setTitle}
              className="story-title"
              placeholder="Showcase your impact with an attention-grabbing title ..."
            />
            <StyledTrueImpactText
              variant="body"
              textareaBackgroundColor={'whiteBase'}
            >
              <IndicatorTextareaRender
                id="story-description"
                questionId={NARRATIVE_STORY_QUESTION_ID}
                handleSave={(e) => {
                  setDescription(e);
                  setError(e.length < 1 ? 'Description is required' : '');
                }}
                initialValue={description}
                placeholder="Don't be shy! Now is the time to brag about all about the amazing work your program does ..."
                textareaClassName={error ? 'textarea-error' : undefined}
                maxLength={900}
              />
            </StyledTrueImpactText>
          </div>
        </StyledDiv>
        {!hideBottomBar && (
          <BottomBar
            disabled={false}
            hideBack={false}
            hideNext={!isValid(title, description)}
            handleBack={handlePrevious}
            handleNext={handleSubmit}
            backTitle="Previous Section"
            nextTitle="Save & Continue"
          />
        )}
      </>
    );
  },
);

export default NarrativeStory;
